import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Spinner,
  Alert,
  Chip,
  EmptyState,
  Select,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type MissingManufacturerRow,
} from '../lib/adminApiClient';

/**
 * Missing-manufacturer worklist (size-discovery plan step 3, Rod 2026-08-23).
 *
 * bg-jobs `manufacturerCoverage` keeps the rows' counts fresh; this screen is
 * where Rod works the list — biggest manufacturers first — recording where
 * each one's product data lives (source_url + kind) and moving its status
 * missing → identified → adapter_built (or no_public_data). The identified
 * rows drive which manufacturer adapters get built (Campbell's open WP REST
 * API is the template: /wp-json/wp/v2/product).
 */

const STATUSES = ['missing', 'identified', 'adapter_built', 'no_public_data'] as const;
const KINDS = ['jsonld', 'feed', 'api', 'scrape', 'none'] as const;

const STATUS_TONE: Record<string, 'warning' | 'positive'> = {
  missing: 'warning',
  identified: 'positive',
  adapter_built: 'positive',
  no_public_data: 'warning',
};

export default function MissingManufacturers() {
  const caps = useCapabilities();
  const canEdit = caps.has('product:edit');
  const [rows, setRows] = useState<MissingManufacturerRow[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = useState<string>('missing');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { sourceUrl: string; sourceKind: string; notes: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listMissingManufacturers(statusFilter);
      setRows(res.rows);
      setStatusCounts(res.statusCounts);
    } catch (e) {
      setError(e instanceof AdminApiError ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const draftFor = (r: MissingManufacturerRow) =>
    drafts[r.id] ?? { sourceUrl: r.sourceUrl ?? '', sourceKind: r.sourceKind, notes: r.notes ?? '' };

  const save = async (r: MissingManufacturerRow, status?: string) => {
    const d = draftFor(r);
    setSavingId(r.id);
    setFlash(null);
    try {
      await adminApiClient.updateMissingManufacturer(r.id, {
        ...(status ? { status } : {}),
        sourceUrl: d.sourceUrl.trim() || null,
        sourceKind: d.sourceKind,
        notes: d.notes.trim() || null,
      });
      setFlash({ kind: 'success', msg: `${r.manufacturer} saved${status ? ` → ${status}` : ''}` });
      await load();
    } catch (e) {
      setFlash({ kind: 'error', msg: e instanceof AdminApiError ? e.message : 'Save failed' });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', p: 3 }}>
      <Typography component="h1" sx={{ fontSize: 24, fontWeight: 800, mb: 0.5 }}>
        Missing manufacturer data
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#6b7a73', mb: 2 }}>
        Which manufacturers still need a product-data source. Counts refresh nightly; biggest
        first. Mark a row <b>identified</b> once you find where their catalog lives.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        {STATUSES.map((s) => (
          <Button
            key={s}
            ariaLabel={`Filter ${s}`}
            variant={statusFilter === s ? 'contained' : 'outlined'}
            buttonText={`${s} (${statusCounts[s] ?? 0})`}
            onClick={() => setStatusFilter(s)}
            sx={{ textTransform: 'none', fontSize: 12 }}
          />
        ))}
      </Box>

      {flash && (
        <Alert severity={flash.kind === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
          {flash.msg}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Spinner />}
      {!loading && rows.length === 0 && (
        <EmptyState icon="listAlt" title="Nothing here" body={`No manufacturers with status "${statusFilter}".`} />
      )}

      {!loading &&
        rows.map((r) => {
          const d = draftFor(r);
          return (
            <Paper key={r.id} sx={{ p: 2, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
                  {r.manufacturer}
                </Typography>
                <Chip label={`${r.productCount} products`} size="small" />
                <Chip label={`${r.lineCount} lines`} size="small" />
                {r.fdcPresent && <Chip label="in FDC" size="small" color="success" />}
                <Chip
                  label={r.status}
                  size="small"
                  color={STATUS_TONE[r.status] === 'positive' ? 'success' : 'warning'}
                />
              </Box>
              {canEdit && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    label="Source URL"
                    value={d.sourceUrl}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [r.id]: { ...d, sourceUrl: e.target.value } }))
                    }
                    size="small"
                    sx={{ flex: 1, minWidth: 260 }}
                    placeholder="e.g. https://www.campbellsfoodservice.com/wp-json/wp/v2/product"
                  />
                  <Select
                    ariaLabel={`Source kind for ${r.manufacturer}`}
                    label="Kind"
                    value={d.sourceKind}
                    onChange={(_e, v) =>
                      setDrafts((prev) => ({ ...prev, [r.id]: { ...d, sourceKind: v as string } }))
                    }
                    options={KINDS.map((k) => ({ value: k, label: k }))}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Notes"
                    value={d.notes}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [r.id]: { ...d, notes: e.target.value } }))
                    }
                    size="small"
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Button
                    ariaLabel={`Mark ${r.manufacturer} identified`}
                    variant="contained"
                    buttonText={savingId === r.id ? 'Saving…' : 'Mark identified'}
                    disabled={savingId === r.id || !d.sourceUrl.trim()}
                    onClick={() => void save(r, 'identified')}
                    sx={{ textTransform: 'none' }}
                  />
                  <Button
                    ariaLabel={`Mark ${r.manufacturer} no public data`}
                    variant="outlined"
                    buttonText="No public data"
                    disabled={savingId === r.id}
                    onClick={() => void save(r, 'no_public_data')}
                    sx={{ ...NEUTRAL_BTN, textTransform: 'none' }}
                  />
                </Box>
              )}
            </Paper>
          );
        })}
    </Box>
  );
}
